/* aw-feature-videos.js (E23, 2026-09-17)
 * Overlay player for the order page feature-video strip (sections/aw-feature-videos.liquid).
 * - Zero video bytes until a chip is tapped: the <video> is preload="none" and only gets a src here.
 * - One clip at a time, sound off until the sound button is tapped, chip tabs, auto-advance on end,
 *   close on X / Escape / swipe down, body scroll lock, focus returned to the chip, scroll preserved.
 * - Events go through window.awTrackGate (the ZIP gate's four-sink tracker: dataLayer, gtag, fbq,
 *   Shopify analytics) with gate:'feature_video'; a bare dataLayer push is the fallback when the gate
 *   script is absent. Payloads carry the clip title and index only, never anything about the visitor.
 * - The CTA is the page's own next step: the ZIP gate before a served ZIP, the wizard after one.
 */
(function () {
  'use strict';
  var root = document.querySelector('[data-aw-fv]');
  if (!root) return;
  var overlay = document.getElementById(root.getAttribute('data-overlay'));
  var chips = Array.prototype.slice.call(root.querySelectorAll('.aw-fv__chip'));
  if (!overlay || !chips.length) return;

  var video = overlay.querySelector('.aw-fvo__video');
  var stage = overlay.querySelector('.aw-fvo__stage');
  var tabs = Array.prototype.slice.call(overlay.querySelectorAll('.aw-fvo__tab'));
  var segs = Array.prototype.slice.call(overlay.querySelectorAll('.aw-fvo__seg b'));
  var titleEl = overlay.querySelector('.aw-fvo__title');
  var capEl = overlay.querySelector('.aw-fvo__cap');
  var countEl = overlay.querySelector('.aw-fvo__count');
  var soundBtn = overlay.querySelector('.aw-fvo__sound');
  var closeBtn = overlay.querySelector('.aw-fvo__close');
  var tapBtn = overlay.querySelector('.aw-fvo__tap');
  var cta = overlay.querySelector('.aw-fvo__cta');
  if (!video || !stage || !soundBtn || !closeBtn || !tapBtn || !cta) return;

  var current = -1;
  var fired5 = false;
  var lastFocus = null;
  var savedScroll = 0;
  var isOpen = false;

  function track(name, params) {
    var payload = Object.assign({ gate: 'feature_video', location: 'order' }, params || {});
    try {
      if (typeof window.awTrackGate === 'function') { window.awTrackGate(name, payload); return; }
    } catch (_) { /* measurement never breaks playback */ }
    try { (window.dataLayer = window.dataLayer || []).push(Object.assign({ event: name }, payload)); } catch (_) {}
  }

  function served() {
    if (document.body.classList.contains('aw-flow-active')) return true;
    try {
      var d = JSON.parse(localStorage.getItem('aw_delivery'));
      return !!(d && (d.state === 'served' || d.state === 'surcharge'));
    } catch (_) { return false; }
  }

  function clipName(i) {
    return chips[i] ? (chips[i].getAttribute('data-title') || ('clip ' + (i + 1))) : '';
  }

  function lockScroll() {
    savedScroll = window.pageYOffset || document.documentElement.scrollTop || 0;
    document.body.style.top = (-savedScroll) + 'px';
    document.body.classList.add('aw-fvo-lock');
  }

  function unlockScroll() {
    document.body.classList.remove('aw-fvo-lock');
    document.body.style.top = '';
    try { window.scrollTo({ top: savedScroll, left: 0, behavior: 'instant' }); }
    catch (_) { window.scrollTo(0, savedScroll); }
  }

  function setProgress(p) {
    if (segs[current]) segs[current].style.setProperty('--p', String(Math.max(0, Math.min(1, p))));
  }

  function load(i, source) {
    if (i < 0 || i >= chips.length) return;
    var chip = chips[i];
    var src = chip.getAttribute('data-src');
    if (!src) return;
    current = i;
    fired5 = false;
    segs.forEach(function (s, k) { s.classList.toggle('is-done', k < i); s.style.setProperty('--p', '0'); });
    tabs.forEach(function (t, k) {
      t.setAttribute('aria-selected', k === i ? 'true' : 'false');
      t.tabIndex = k === i ? 0 : -1;
    });
    if (tabs[i] && tabs[i].scrollIntoView) {
      try { tabs[i].scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'instant' }); } catch (_) {}
    }
    titleEl.textContent = chip.getAttribute('data-title') || '';
    capEl.textContent = chip.getAttribute('data-caption') || '';
    countEl.textContent = (i + 1) + ' of ' + chips.length;
    var poster = chip.getAttribute('data-poster');
    if (poster) video.setAttribute('poster', poster); else video.removeAttribute('poster');
    overlay.classList.remove('is-paused');
    video.src = src;
    video.load();
    var p = video.play();
    if (p && p.catch) p.catch(function () { overlay.classList.add('is-paused'); });
    track('feature_video_open', { clip: clipName(i), index: i, source: source || 'chip' });
  }

  function open(i, source) {
    lastFocus = document.activeElement;
    var isServed = served();
    cta.textContent = isServed ? overlay.getAttribute('data-cta-served') : overlay.getAttribute('data-cta-unserved');
    cta.setAttribute('href', isServed ? '#AwGuidedFlow' : '#aw-d-hero');
    lockScroll();
    overlay.hidden = false;
    isOpen = true;
    load(i, source);
    try { closeBtn.focus({ preventScroll: true }); } catch (_) { closeBtn.focus(); }
  }

  function close(reason) {
    if (!isOpen) return;
    track('feature_video_close', { clip: clipName(current), index: current, watched_s: Math.round(video.currentTime || 0), reason: reason || 'button' });
    try { video.pause(); } catch (_) {}
    video.removeAttribute('src');
    video.removeAttribute('poster');
    try { video.load(); } catch (_) {}
    overlay.hidden = true;
    isOpen = false;
    unlockScroll();
    // iOS Safari does not focus a tapped button, so lastFocus is often <body>; the chip is the
    // right place to land in that case.
    var back = (lastFocus && lastFocus !== document.body && lastFocus.focus) ? lastFocus : (chips[current] || chips[0]);
    try { back.focus({ preventScroll: true }); } catch (_) {}
  }

  chips.forEach(function (chip, i) {
    chip.addEventListener('click', function () { open(i, 'chip'); });
  });
  tabs.forEach(function (tab) {
    tab.addEventListener('click', function () { load(Number(tab.getAttribute('data-index')), 'tab'); });
  });
  closeBtn.addEventListener('click', function () { close('button'); });

  soundBtn.addEventListener('click', function () {
    video.muted = !video.muted;
    soundBtn.setAttribute('aria-pressed', video.muted ? 'false' : 'true');
    soundBtn.setAttribute('aria-label', video.muted ? 'Turn sound on' : 'Turn sound off');
    overlay.classList.toggle('has-sound', !video.muted);
    track('feature_video_sound', { clip: clipName(current), index: current, on: !video.muted });
  });

  tapBtn.addEventListener('click', function () {
    if (video.paused) {
      var p = video.play();
      if (p && p.catch) p.catch(function () {});
      overlay.classList.remove('is-paused');
    } else {
      video.pause();
      overlay.classList.add('is-paused');
    }
  });

  cta.addEventListener('click', function (e) {
    e.preventDefault();
    var target = cta.getAttribute('href');
    track('feature_video_cta', { clip: clipName(current), index: current, target: target });
    close('cta');
    var el = document.querySelector(target);
    if (el) { try { el.scrollIntoView({ behavior: 'smooth', block: 'start' }); } catch (_) { el.scrollIntoView(); } }
    var input = document.getElementById('aw-zip-input');
    if (target === '#aw-d-hero' && input) {
      setTimeout(function () { try { input.focus({ preventScroll: true }); } catch (_) {} }, 450);
    }
  });

  video.addEventListener('timeupdate', function () {
    if (!isOpen) return;
    var d = video.duration;
    if (d && isFinite(d)) setProgress(video.currentTime / d);
    if (!fired5 && video.currentTime >= 5) {
      fired5 = true;
      track('feature_video_5s', { clip: clipName(current), index: current });
    }
  });
  video.addEventListener('ended', function () {
    track('feature_video_complete', { clip: clipName(current), index: current });
    setProgress(1);
    if (current + 1 < chips.length) load(current + 1, 'auto');
    else overlay.classList.add('is-paused');
  });
  video.addEventListener('pause', function () { if (isOpen && !video.ended && !video.seeking) overlay.classList.add('is-paused'); });
  video.addEventListener('play', function () { overlay.classList.remove('is-paused'); });
  video.addEventListener('error', function () {
    if (!isOpen) return;
    capEl.textContent = 'This clip could not load. Tap another.';
    overlay.classList.add('is-paused');
  });

  document.addEventListener('keydown', function (e) {
    if (!isOpen) return;
    if (e.key === 'Escape') { e.preventDefault(); close('escape'); }
    else if (e.key === 'ArrowRight' && current + 1 < chips.length) { e.preventDefault(); load(current + 1, 'key'); }
    else if (e.key === 'ArrowLeft' && current > 0) { e.preventDefault(); load(current - 1, 'key'); }
    else if (e.key === 'Tab') {
      var f = overlay.querySelectorAll('button:not([tabindex="-1"]), a[href]');
      if (!f.length) return;
      var first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  });

  // Story conventions on the stage only (the tabs row scrolls on its own): swipe down closes,
  // horizontal swipes switch clips. Listeners are passive; nothing here blocks scrolling.
  var tx = 0, ty = 0, tt = 0;
  stage.addEventListener('touchstart', function (e) {
    var t = e.changedTouches[0]; tx = t.clientX; ty = t.clientY; tt = Date.now();
  }, { passive: true });
  stage.addEventListener('touchend', function (e) {
    var t = e.changedTouches[0];
    var dx = t.clientX - tx, dy = t.clientY - ty;
    if (Date.now() - tt > 600) return;
    if (dy > 90 && Math.abs(dx) < 60) close('swipe');
    else if (dx < -70 && Math.abs(dy) < 50 && current + 1 < chips.length) load(current + 1, 'swipe');
    else if (dx > 70 && Math.abs(dy) < 50 && current > 0) load(current - 1, 'swipe');
  }, { passive: true });

  document.addEventListener('visibilitychange', function () {
    if (document.hidden && isOpen && !video.paused) video.pause();
  });
})();
